import { Random } from './Random';

/**
 * TODO: CardsHelper should be a helper for the Redux state and not a stand-alone singleton.
 */
export default class CardsHelper {
    public static instance: CardsHelper;

    public totalCardValues: number[];
    public availableCardValues: number[];
    public inPlayCards: number[];

    constructor() {
        this.totalCardValues = [];
        this.availableCardValues = [];
        this.inPlayCards = [];

        CardsHelper.instance = this;
    }

    /**
     * Get a random or specified card from the available cards. If the value is not found a new deck is added.
     * @param cardValue Optional card value to draw from available cards.
     * @return The card value drawn.
     */
    public draw(cardValue?: number): number {
        // If no card value specified draw a random card value.
        if (cardValue === undefined) {
            // No cards left - add another deck.
            if (this.availableCardValues.length === 0) {
                this.addDeck();
            }

            cardValue = this.availableCardValues[
                Math.floor(Random.value() * this.availableCardValues.length)
            ];
        } else {
            // Wrap the value to keep it in bounds.
            cardValue = wrap(cardValue, DeckSize);

            // Card value not found - add another deck.
            if (this.availableCardValues.indexOf(cardValue) === -1) {
                this.addDeck();
            }
        }

        // Remove from available cards.
        const index = this.availableCardValues.indexOf(cardValue);
        if (index >= 0) this.availableCardValues.splice(index, 1);

        // Move card to in-play.
        this.inPlayCards.push(cardValue);

        return cardValue;
    }

    public drawMatch(cardValue: number): number {
        const possibleMatches = this.getPossibleMatches(cardValue);
        const availableMatches = possibleMatches.filter(
            x => this.availableCardValues.indexOf(x) !== -1
        );

        if (availableMatches.length > 0) {
            return this.draw(Random.getRandomItem(availableMatches));
        } else {
            return this.draw(Random.getRandomItem(possibleMatches));
        }
    }

    /**
     * Remove the card from in-play and make available again (un-shifted to bottom of available cards array).
     * @param cardValue
     */
    public discard(cardValue?: number) {
        const index = this.inPlayCards.indexOf(cardValue);
        if (index >= 0) this.inPlayCards.splice(index, 1);
        this.availableCardValues.unshift(cardValue);
    }

    private addDeck(): void {
        const newDeck: number[] = generateDeck();
        this.totalCardValues = this.totalCardValues.concat(newDeck);
        this.availableCardValues = this.availableCardValues.concat(newDeck);
    }

    private getPossibleMatches(cardValue: number): number[] {
        const prevMatches = new Array(SuitSize)
            .fill(wrap(cardValue - 1, RankSize))
            .map((v, i) => v + RankSize * i);
        const nextMatches = new Array(SuitSize)
            .fill(wrap(cardValue + 1, RankSize))
            .map((v, i) => v + RankSize * i);

        return prevMatches.concat(nextMatches);
    }
}

/**
 * Wrap the specified value against the specified max. Also works for negative numbers.
 * @param value
 * @param max
 */
export function wrap(value: number, max: number) {
    return ((value % max) + max) % max;
}

/**
 * Unpack a fresh new deck of cards.
 */
export function generateDeck(): number[] {
    const cards: number[] = [];
    for (let i = 0; i < DeckSize; i++) {
        cards.push(i);
    }
    return cards;
}

/**
 * Returns the shortest absolute difference between card values. Example:
 * Ace -> King = 1
 * King -> Ace = 1
 * Ace -> Two = 1
 * Two -> King = 2
 * Ten -> Three = 6
 * @param a
 * @param b
 */
export function valueDiff(a: number, b: number): number {
    a = a % RankSize;
    b = b % RankSize;

    let diff = a - b;
    if (diff > 6) diff -= RankSize;
    if (diff < -6) diff += RankSize;

    return Math.abs(diff);
}

export enum Suit {
    CLUBS = 0,
    HEARTS = 1,
    SPADES = 2,
    DIAMONDS = 3,
}

export const SuitByValue: any = {
    0: 'CLUBS',
    1: 'HEARTS',
    2: 'SPADES',
    3: 'DIAMONDS',
};

export enum CardValue {
    ACE = 0,
    TWO = 1,
    THREE = 2,
    FOUR = 3,
    FIVE = 4,
    SIX = 5,
    SEVEN = 6,
    EIGHT = 7,
    NINE = 8,
    TEN = 9,
    JACK = 10,
    QUEEN = 11,
    KING = 12,
}

export const CardByValue: any = {
    0: 'ACE',
    1: 'TWO',
    2: 'THREE',
    3: 'FOUR',
    4: 'FIVE',
    5: 'SIX',
    6: 'SEVEN',
    7: 'EIGHT',
    8: 'NINE',
    9: 'TEN',
    10: 'JACK',
    11: 'QUEEN',
    12: 'KING',
};

export enum CardColor {
    Red,
    Black,
}

export const cardColorBySuit: Record<Suit, CardColor> = {
    [Suit.HEARTS]: CardColor.Red,
    [Suit.DIAMONDS]: CardColor.Red,
    [Suit.CLUBS]: CardColor.Black,
    [Suit.SPADES]: CardColor.Black,
};

export const SuitSize: number = 4;
export const RankSize: number = 13;
export const DeckSize: number = 52;

export function cardValueToString(value?: number): string {
    return `${CardByValue[value % RankSize]} of ${
        SuitByValue[Math.floor(value / RankSize)]
    }`;
}

export function getCardValue(value?: number): CardValue {
    return value % RankSize;
}

export function getCardSuit(value: number): Suit {
    return Math.floor(value / RankSize);
}
